'use client'

import { useMemo } from 'react'

import { Box, Grid } from '@mui/joy'
import { pxToRem } from '@ui/style/muiTheme'
import UIDivider from '@components/ui/UIDivider'
import YoutubeContentCard from '@components/content/YoutubeContentCard'
import { ArticleItemProps } from '@components/list'
import Section from '@components/common/Section/Section'
import UIContainer from '@components/ui/UIContainer'
import UISns from '@components/ui/UISns'
import { CommonAreaResponseVo, HomeAreaSequenceResponseVo } from '@schemas/non-auth'
import { useRefetchYoutubeSns } from '@services/api/NewsHome/YoutubeSns'

export type YoutubeSnsSectionData = HomeAreaSequenceResponseVo & CommonAreaResponseVo

interface SectionProps {
	data: YoutubeSnsSectionData
}

const YoutubeSnsSection = ({ data }: SectionProps) => {
	const hasData = data.areaItemList && data.areaItemList.length > 0
	const hasInitialYoutubeLiveUrl =
		hasData && Boolean(data.areaItemList[0]?.liveInfo?.youtubeLiveUrl)
	const { data: refetchData, isLoading } = useRefetchYoutubeSns(
		{ areaIdx: data.areaIdx },
		undefined,
		hasInitialYoutubeLiveUrl,
	)

	const { bgMoImgUrl, bgTbImgUrl, bgPcImgUrl } = data
	const src = bgMoImgUrl && bgTbImgUrl && bgPcImgUrl && { bgMoImgUrl, bgTbImgUrl, bgPcImgUrl }
	const modifyData = useMemo(() => {
		if (!hasData) return null

		const areaItemList = data.areaItemList[0]
		if (!areaItemList || !areaItemList.liveInfo) {
			return null
		}

		return {
			articleIdx: areaItemList.articleInfo,
			articleInnerTextContent: areaItemList.itemContent,
			articleThumbnailImgUrl: '',
			articleTitle: areaItemList.itemTitle,
			bulletType: areaItemList.liveInfo?.liveBulletTypeCode,
			code: areaItemList.itemCode.code,
			liveTagTypeCode: areaItemList.liveInfo?.liveTagTypeCode,
			publicationDate: areaItemList.liveInfo?.liveStartDate,
			youtubeLiveUrl: areaItemList.liveInfo?.youtubeLiveUrl,
			liveIdx: areaItemList.liveInfo?.liveIdx,
			articleMobileTitle: areaItemList.itemTitle,
		}
	}, [data, hasData])

	if (data.templateIdx === 27) {
		if (!hasData || (hasInitialYoutubeLiveUrl && isLoading)) {
			return null
		}

		const youtubeLiveUrl = hasInitialYoutubeLiveUrl
			? refetchData?.data?.areaItemList?.[0]?.liveInfo?.youtubeLiveUrl
			: null

		if (!youtubeLiveUrl || !modifyData) {
			return null
		}
	}

	return (
		<Section
			bgColor={data.isStaticBackgroundColor}
			{...(!data.isStaticBackgroundColor && src && { src, className: 'set-bg has-title' })}
			hasTitle
			templateIdx={data.templateIdx}
		>
			<UIContainer>
				<Grid
					container
					columnSpacing={{ xs: 0, md: pxToRem(24), lg: pxToRem(32) }}
					rowSpacing={{ xs: pxToRem(16), md: 0, lg: 0 }}
					alignItems="flex-start"
					className="list-LGimageList"
					sx={{
						marginTop: 0,
						paddingTop: 0,
						paddingBottom: 0,
					}}
				>
					<Grid
						xs={12}
						md={8}
						lg={7.4}
						xl={7}
						display={{ xs: 'block', md: 'flex' }}
						alignItems="stretch"
						sx={{
							paddingTop: 0,
							paddingBottom: 0,
						}}
					>
						{data.additionalImg1Url && data.templateIdx === 28 ? (
							<Box
								sx={{
									width: '100%',
									height: {
										xl: pxToRem(507),
										lg: pxToRem(426),
										md: pxToRem(374),
										xs: '100%',
									},
									paddingBottom: { xs: '56.25%', md: 0 },
									backgroundImage: `url(${data.additionalImg1Url})`,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
								}}
							/>
						) : (
							modifyData && (
								<YoutubeContentCard
									article1st={modifyData as unknown as ArticleItemProps}
								/>
							)
						)}
					</Grid>
					<UIDivider
						orientation="vertical"
						sx={{ display: { xs: 'none', md: 'block', lg: 'block' } }}
						isDark={false}
					/>
					<Grid xs={12} md={4} lg={4.6} xl={5} sx={{ paddingTop: 0, paddingBottom: 0 }}>
						<UISns data={data.snsPostXList} />
					</Grid>
				</Grid>
			</UIContainer>
		</Section>
	)
}

export default YoutubeSnsSection
